<template>
    <div>
        <!--Edit Document Dialog-->
        <Dialog :visible.sync="documentDialog" :style="{width: '450px'}" header="Curriculum Vitae Details" :modal="true"
                :maximizable="true"
                class="p-fluid">

            <div class="p-field" v-if="createDocument">
                <label for="staff">Staff</label>
                <Dropdown id="staff" v-model="document.staff"
                          :options="staffs" optionLabel="name" :filter="true"
                          :class="{'p-invalid': submitted && $v.document.staff.$invalid}"/>
                <small class="p-invalid" v-if="submitted && $v.document.staff.$invalid">Staff is
                    required.</small>
            </div>

            <div class="p-field">
                <label for="name">Name</label>
                <InputText id="name" v-model.trim="document.name" required="true" autofocus
                           placeholder="e.g. CV MMMM_DD_YY"
                           :class="{'p-invalid': submitted && $v.document.name.$invalid}"/>
                <small class="p-invalid" v-if="submitted && $v.document.name.$invalid">Name is required.</small>
            </div>

            <div class="p-field">
                <label for="description">Description</label>
                <InputText id="description" v-model.trim="document.description" required="true"
                           placeholder="e.g. CV for bidding Agreement No. 123456"/>
            </div>

            <div class="p-field" v-if="adminMode">
                <label for="admin">Project / General</label>
                <SelectButton id="admin" v-model.trim="document['is-admin']"
                              :options="[{key: 0, value: 'General'}, {key: 1, value: 'Project'}]"
                              optionLabel="value" optionValue="key" required="true"/>
            </div>

            <template #footer>
                <Button label="Cancel" icon="pi pi-times" class="p-button-text" @click="hideDialog"/>
                <Button label="Save" icon="pi pi-check" class="p-button-text" @click="saveDocument"/>
            </template>
        </Dialog>

        <!--Delete Document Dialog-->
        <Dialog :visible.sync="deleteDocumentDialog" :style="{width: '450px'}" header="Confirm" :modal="true">
            <div class="confirmation-content">
                <i class="pi pi-exclamation-triangle p-mr-3" style="font-size: 2rem"/>
                <span v-if="document">Are you sure you want to delete <b>{{ document.name }}</b>?</span>
            </div>
            <template #footer>
                <Button label="No" icon="pi pi-times" class="p-button-text"
                        @click="deleteDocumentDialog = false"/>
                <Button label="Yes" icon="pi pi-check" class="p-button-text" @click="deleteDocument"/>
            </template>
        </Dialog>

        <!--Delete Documents Dialog-->
        <Dialog :visible.sync="deleteDocumentsDialog" :style="{width: '450px'}" header="Confirm" :modal="true">
            <div class="confirmation-content">
                <i class="pi pi-exclamation-triangle p-mr-3" style="font-size: 2rem"/>
                <span v-if="document">Are you sure you want to delete the selected document?</span>
            </div>
            <template #footer>
                <Button label="No" icon="pi pi-times" class="p-button-text"
                        @click="deleteDocumentsDialog = false"/>
                <Button label="Yes" icon="pi pi-check" class="p-button-text"
                        @click="deleteSelectedDocuments"/>
            </template>
        </Dialog>

        <!--Verify Documents Dialog-->
        <Dialog :visible.sync="toggleDocumentsDialog" :style="{width: '450px'}" header="Confirm" :modal="true">
            <div class="confirmation-content">
                <i class="pi pi-exclamation-triangle p-mr-3" style="font-size: 2rem"/>
                <span v-if="document">Are you sure you want to {{ verifyDocuments ? 'verify' : 'unverify' }} the selected document?</span>
            </div>
            <template #footer>
                <Button label="No" icon="pi pi-times" class="p-button-text"
                        @click="toggleDocumentsDialog = false"/>
                <Button label="Yes" icon="pi pi-check" class="p-button-text"
                        @click="toggleSelectedDocuments"/>
            </template>
        </Dialog>
    </div>
</template>

<script>
import {Component, Prop, Vue, Watch} from "vue-property-decorator";

Component.registerHooks(['validations']);
import {required, email} from 'vuelidate/lib/validators'
import Toast from 'primevue/toast';
import Dialog from "primevue/dialog";
import Dropdown from 'primevue/dropdown';
import SelectButton from 'primevue/selectbutton';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import AutoComplete from 'primevue/autocomplete';
import StaffService from "@/service/StaffService";
import DocumentService from "@/service/DocumentService";
import InputSwitch from 'primevue/inputswitch';
import store from "../../store";

/**
 * Class-component
 */
@Component({
    components: {
        Toast: Vue.extend(Toast),
        Dialog: Vue.extend(Dialog),
        Dropdown: Vue.extend(Dropdown),
        SelectButton: Vue.extend(SelectButton),
        InputText: Vue.extend(InputText),
        Button: Vue.extend(Button),
        AutoComplete: Vue.extend(AutoComplete),
        InputSwitch: Vue.extend(InputSwitch),
    }
})
export default class DocumentDialog extends Vue {
    @Prop({type: Boolean, default: false}) adminMode;
    createDocument = false;
    staffService = new StaffService();
    staffs = [];
    documentService = new DocumentService();
    documentDialog = false;
    deleteDocumentDialog = false;
    deleteDocumentsDialog = false;
    toggleDocumentsDialog = false;
    verifyDocuments = false;
    document = {};
    submitted = false;
    selectedDocuments = [];
    files = [];

    toastSuccess = {
        severity: 'success',
        summary: 'Successful',
        detail: '',
        life: 3000
    };
    toastError = {
        severity: 'error',
        summary: 'Error',
        detail: '',
        life: 3000
    };

    validations() {
        return {
            document: {
                staff: {required},
                name: {required},
            }
        }
    }

    @Watch('createDocument')
    async onCreateDocumentChanged() {
        if (this.createDocument) this.staffs = await this.staffService.getStaffs();
    }

    hideDialog() {
        this.documentDialog = false;
        this.submitted = false;
    }

    async saveDocument() {
        this.submitted = true;

        if (this.$v.$invalid) {
            return; // invalid input
        }

        const documentObj = {
            'is-admin': false,
            'verified': 0,
            _jv: {
                type: 'documents',
                relationships: {
                    staff: {data: {id: this.document.staff._jv.id, type: 'staffs'}}
                }
            }
        };

        ['name', 'description'].forEach(el => {
            documentObj[el] = this.document[el]
        });

        // only allow admin to update this field
        if (this.adminMode) documentObj['is-admin'] = this.document['is-admin']

        try {
            if (this.document._jv) {
                // PATCH
                documentObj._jv.id = this.document._jv.id
                await this.documentService.updateDocument(documentObj)
            } else {
                // POST
                await this.documentService.createDocument(documentObj);
            }

            this.$emit('updated')
            this.toastSuccess.detail = 'Curriculum Vitae Updated'
            this.$toast.add(this.toastSuccess);
        } catch (e) {
            this.toastError.detail = e.message
            this.$toast.add(this.toastError);
            this.documentService.logError(e).forEach(err => {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Details',
                    detail: err,
                });
            })
        }

        this.documentDialog = false;
        this.document = {};
    }

    async deleteDocument() {
        try {
            await this.documentService.deleteDocument(this.document._jv.id)
            this.$emit('updated')
            this.toastSuccess.detail = 'Curriculum Vitae Deleted'
            this.$toast.add(this.toastSuccess);
        } catch (e) {
            this.toastError.detail = e.message
            this.$toast.add(this.toastError);
            this.documentService.logError(e).forEach(err => {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Details',
                    detail: err,
                });
            })
        }

        this.deleteDocumentDialog = false;
        this.document = {};
    }

    async deleteSelectedDocuments() {
        try {
            for (const deleteDocument of this.selectedDocuments) {
                await this.documentService.deleteDocument(deleteDocument._jv.id)
            }
            this.$emit('updated')
            this.toastSuccess.detail = 'Curriculum Vitae Deleted'
            this.$toast.add(this.toastSuccess);
        } catch (e) {
            this.toastError.detail = e.message
            this.$toast.add(this.toastError);
            this.documentService.logError(e).forEach(err => {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Details',
                    detail: err,
                });
            })
        }

        this.deleteDocumentsDialog = false;
        this.selectedDocuments = [];
    }

    async toggleSelectedDocuments() {
        try {
            if (this.verifyDocuments) {
                // verify
                for (const verifyDocument of this.selectedDocuments) {
                    const documentObj = {
                        'verified': 1,
                        'verified-by': store.state.staffId,
                        'verified-at': this.$moment().toISOString(),
                        _jv: {
                            id: verifyDocument._jv.id,
                            type: 'documents',
                            relationships: {
                                staff: {data: {id: verifyDocument.staff._jv.id, type: 'staffs'}}
                            }
                        }
                    };
                    await this.documentService.updateDocument(documentObj)
                }
            }

            if (!this.verifyDocuments) {
                // unverify
                for (const verifyDocument of this.selectedDocuments) {
                    const documentObj = {
                        'verified': 0,
                        'verified-by': null,
                        'verified-at': null,
                        _jv: {
                            id: verifyDocument._jv.id,
                            type: 'documents',
                            relationships: {
                                staff: {data: {id: verifyDocument.staff._jv.id, type: 'staffs'}}
                            }
                        }
                    };
                    await this.documentService.updateDocument(documentObj)
                }
            }

            this.$emit('updated')
            this.toastSuccess.detail = 'Curriculum Vitae Updated'
            this.$toast.add(this.toastSuccess);
        } catch (e) {
            this.toastError.detail = e.message
            this.$toast.add(this.toastError);
            this.documentService.logError(e).forEach(err => {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Details',
                    detail: err,
                });
            })
        }

        this.toggleDocumentsDialog = false;
        this.selectedDocuments = [];
    }
}


</script>

<style scoped>

</style>